import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { type AuthState } from '@/common/types/auth.types';

const baseURL = import.meta.env.VITE_API_EDMA_BASE_URL;

export type PersonnelResponse = {
  id: string;
  deleted: boolean;
  user_id: string;
  updated_at: string;
  created_at: string;
  group_id: string;
  incident_id: string;
  name: string;
  organization: string;
  position: string;
  phone_number: string;
  email: string;
  benefit_rate: string;
  hourly_rate: string;
  overtime_rate: string;
  overtime_benefit_rate: string;
};

export interface PersonnelListQuery {
  limit: number;
  offset: number;
  search?: string;
  incidentId?: string;
  groupId?: number;
  sort?: 'name' | 'updated_at';
  order?: 'asc' | 'desc';
}

export interface PersonnelPayload {
  incident_id: string | number;
  group_id: number;
  name: string;
  position: string;
  email: string;
  phone_number: string;
  organization: string;
  user_id: string;
  id?: string;
}

export const personnelApi = createApi({
  reducerPath: 'personnelApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseURL}/api/personnel`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as AuthState).auth.tokenVerification?.accessToken;
      if (token) {
        headers.set('Authorization', token);
      }
      return headers;
    },
  }),
  tagTypes: ['Personnel', 'PersonnelList'],
  endpoints: (builder) => ({
    getPersonnels: builder.query<
      { count: number; dataset: PersonnelResponse[] },
      PersonnelListQuery
    >({
      queryFn: async (arg, { getState }, _extraOptions, baseQuery) => {
        const token = (getState() as AuthState).auth.tokenVerification?.accessToken;
        const { incidentId, search, groupId, ...restParams } = arg;
        const result = await baseQuery({
          url: '',
          params: {
            access_token: token,
            ...restParams,
            ...(search ? { 'filters[search]': search } : {}),
            ...(incidentId ? { 'filters[pvIncidentID]': incidentId } : {}),
            ...(groupId ? { 'filters[group_id]': groupId } : {}),
          },
        });
        return result as { data: { count: number; dataset: PersonnelResponse[] } };
      },
      providesTags: ['PersonnelList'],
    }),
    getPersonnel: builder.query<PersonnelResponse, string>({
      queryFn: async (userId, { getState }, _extraOptions, baseQuery) => {
        const token = (getState() as AuthState).auth.tokenVerification?.accessToken;
        const result = await baseQuery({
          url: `/${userId}`,
          params: { access_token: token },
        });
        return result as { data: PersonnelResponse };
      },
      providesTags: (_result, _error, id) => [{ type: 'Personnel', id }],
    }),
    savePersonnel: builder.mutation<PersonnelResponse, PersonnelPayload>({
      query: (body) => ({
        url: body?.id ? `/${body.id}` : '',
        method: body?.id ? 'PUT' : 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, body) => [
        'PersonnelList',
        ...(body.id ? [{ type: 'Personnel' as const, id: body.id }] : []),
      ],
    }),
    deletePersonnel: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        'PersonnelList',
        { type: 'Personnel', id },
      ],
    }),
  }),
});

export const {
  useGetPersonnelQuery,
  useGetPersonnelsQuery,
  useSavePersonnelMutation,
  useDeletePersonnelMutation,
} = personnelApi;
